import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, HelpCircle, BookOpen, Command, Loader2, ArrowRight, Sparkles, Inbox, Keyboard as KeyboardIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * HelpPalette — sibling to GlobalQuickActions' SearchPalette. Focused on
 * "how do I…?" questions with three surfaces:
 *   1. Curated KB topics (steps + jump-to-page CTA)
 *   2. Keyboard shortcuts cheat sheet
 *   3. Ask AI — LLM fallback (Claude Sonnet via Emergent LLM key)
 *
 * Mounted by GlobalQuickActions when `mode === "help"`. Fully client-side
 * search over the KB after a single hydration fetch on mount.
 */
export default function HelpPalette({ onClose }) {
  const navigate = useNavigate();
  const [kb, setKb] = useState({ topics: [], shortcuts: [] });
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("topics"); // topics | shortcuts | ai
  const [aiState, setAiState] = useState({ loading: false, answer: null, suggestedPath: null, suggestedTitle: null, used_llm: false });
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  // Hydrate KB once
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/help/kb`)
      .then(r => r.ok ? r.json() : { topics: [], shortcuts: [] })
      .then(d => { if (!cancelled) setKb(d); })
      .catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 30);
    return () => { cancelled = true; };
  }, []);

  // Local KB search — prefix > substring > keyword > steps
  const results = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return kb.topics.slice(0, 8);
    const scored = [];
    for (const t of kb.topics) {
      const title = (t.title || "").toLowerCase();
      const kws = (t.keywords || []).join(" ").toLowerCase();
      const steps = (t.steps || []).join(" ").toLowerCase();
      let s = 0;
      if (title === ql) s = 1000;
      else if (title.startsWith(ql)) s = 500;
      else if (title.includes(ql)) s = 300;
      else if (kws.includes(ql)) s = 200;
      else if (steps.includes(ql)) s = 60;
      else {
        // word-level hits
        const words = ql.split(/\s+/).filter(Boolean);
        s = words.reduce((acc, w) => acc + (title.includes(w) || kws.includes(w) ? 40 : 0), 0);
      }
      if (s > 0) scored.push([s, t]);
    }
    scored.sort((a, b) => b[0] - a[0]);
    return scored.slice(0, 12).map(x => x[1]);
  }, [q, kb.topics]);

  const askAI = async () => {
    if (!q.trim() || aiState.loading) return;
    setAiState({ loading: true, answer: null, suggestedPath: null, suggestedTitle: null, used_llm: false });
    setTab("ai");
    try {
      const r = await fetch(`${API}/api/help/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ q }),
      });
      const d = await r.json();
      setAiState({
        loading: false,
        answer: d.answer || "No answer.",
        suggestedPath: d.suggested_path || null,
        suggestedTitle: d.suggested_title || null,
        used_llm: !!d.used_llm,
      });
    } catch {
      setAiState({ loading: false, answer: "Couldn't reach the AI helper.", suggestedPath: null, suggestedTitle: null, used_llm: false });
    }
  };

  const goTo = (path) => {
    if (path) navigate(path);
    onClose?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") return onClose?.();
    if (tab !== "topics") return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(results.length - 1, i + 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIdx]) goTo(results[activeIdx].path);
      else if (q.trim().length > 3) askAI();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center pt-[8vh] bg-slate-900/60 backdrop-blur-md p-4"
      onClick={onClose}
      data-testid="help-palette"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — bright yellow accent bar to distinguish from Cmd+K */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-[#FFDA00]/15 via-white to-white">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#FFDA00" }}>
            <HelpCircle className="w-4 h-4" style={{ color: "#032866" }} />
          </div>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActiveIdx(0); if (tab === "ai") setTab("topics"); }}
            onKeyDown={onKeyDown}
            placeholder="Ask a question or search a topic… (e.g. how do I post a payment?)"
            className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder-slate-400"
            data-testid="help-palette-input"
          />
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">esc</kbd>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-100 bg-slate-50/60">
          <TabButton active={tab === "topics"} onClick={() => setTab("topics")} icon={BookOpen} label={`Topics${kb.topics.length ? ` · ${kb.topics.length}` : ""}`} testid="help-tab-topics" />
          <TabButton active={tab === "shortcuts"} onClick={() => setTab("shortcuts")} icon={KeyboardIcon} label="Shortcuts" testid="help-tab-shortcuts" />
          <TabButton active={tab === "ai"} onClick={() => askAI()} icon={Sparkles} label="Ask AI" testid="help-tab-ai" disabled={!q.trim()} />
          <div className="flex-1" />
          <span className="text-[10px] font-mono text-slate-400 hidden sm:block">
            <kbd className="px-1 rounded bg-white border border-slate-200">↵</kbd> open · <kbd className="px-1 rounded bg-white border border-slate-200">Esc</kbd> close
          </span>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto" data-testid="help-palette-body">
          {tab === "topics" && (
            <TopicsList results={results} activeIdx={activeIdx} setActiveIdx={setActiveIdx} onGo={goTo} onAskAI={askAI} q={q} />
          )}
          {tab === "shortcuts" && (
            <ShortcutsList shortcuts={kb.shortcuts} />
          )}
          {tab === "ai" && (
            <AiAnswer state={aiState} onGo={goTo} onRetry={askAI} q={q} />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500 bg-slate-50">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Command className="w-3 h-3" /> Press <kbd className="font-mono px-1 bg-white border border-slate-200 rounded">?</kbd> anywhere for Help
            </span>
          </div>
          <button
            onClick={() => goTo("/healthcare/guide")}
            className="text-[11px] font-semibold text-[#032866] hover:underline"
            data-testid="help-palette-open-guide"
          >
            Open full User Guide →
          </button>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${active ? "bg-[#032866] text-white" : "text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"}`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function TopicsList({ results, activeIdx, setActiveIdx, onGo, onAskAI, q }) {
  if (results.length === 0) {
    return (
      <div className="py-10 text-center px-4" data-testid="help-topics-empty">
        <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500 mb-3">No topics match &ldquo;{q}&rdquo;.</p>
        {q.trim().length > 3 && (
          <button
            onClick={onAskAI}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#032866] text-white text-xs font-semibold hover:bg-[#003A8A]"
            data-testid="help-topics-fallback-ask"
          >
            <Sparkles className="w-3.5 h-3.5" /> Ask AI: &ldquo;{q}&rdquo;
          </button>
        )}
      </div>
    );
  }
  return (
    <div>
      {results.map((t, i) => {
        const active = i === activeIdx;
        return (
          <button
            key={t.id}
            onClick={() => onGo(t.path)}
            onMouseEnter={() => setActiveIdx(i)}
            className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${active ? "bg-amber-50" : "hover:bg-slate-50"}`}
            data-testid={`help-topic-${t.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: active ? "#FFDA00" : "#F1F5F9" }}>
                <BookOpen className="w-4 h-4" style={{ color: active ? "#032866" : "#64748B" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-slate-900">{t.title}</p>
                  {t.cta && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-900/80 text-white">
                      {t.cta}
                    </span>
                  )}
                </div>
                <ol className="mt-1 space-y-0.5">
                  {(t.steps || []).slice(0, 3).map((s, idx) => (
                    <li key={idx} className="text-[12px] text-slate-600 leading-snug flex gap-1.5">
                      <span className="font-mono text-slate-400 shrink-0">{idx + 1}.</span>
                      <span className="line-clamp-1">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <ArrowRight className={`w-4 h-4 shrink-0 mt-1 ${active ? "text-amber-600" : "text-slate-300"}`} />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ShortcutsList({ shortcuts }) {
  const grouped = useMemo(() => {
    const g = {};
    for (const s of shortcuts || []) {
      const c = s.context || "global";
      (g[c] = g[c] || []).push(s);
    }
    return g;
  }, [shortcuts]);

  const labels = { global: "Global", "client-file": "Client File", "search-palette": "Inside search palette" };

  return (
    <div className="p-4 space-y-4" data-testid="help-shortcuts-list">
      {Object.entries(grouped).map(([context, items]) => (
        <div key={context}>
          <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 mb-2">{labels[context] || context}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100" data-testid={`help-shortcut-${s.id}`}>
                <span className="text-[12.5px] font-semibold text-slate-800">{s.label}</span>
                <span className="flex items-center gap-1">
                  {(s.keys || []).map((k, i) => (
                    <kbd key={i} className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-700 shadow-sm">
                      {k}
                    </kbd>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AiAnswer({ state, onGo, onRetry, q }) {
  if (state.loading) {
    return (
      <div className="py-14 text-center" data-testid="help-ai-loading">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400 mb-3" />
        <p className="text-xs text-slate-500">Consulting the Avida AI assistant…</p>
      </div>
    );
  }
  if (!state.answer) {
    return (
      <div className="py-14 text-center" data-testid="help-ai-empty">
        <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-3" />
        <p className="text-xs text-slate-500 mb-3">Type a question above then press <kbd className="font-mono px-1 rounded bg-slate-100 border border-slate-200">↵</kbd> or hit Ask AI.</p>
        {q.trim().length > 3 && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#032866] text-white text-xs font-semibold"
            data-testid="help-ai-retry"
          >
            <Sparkles className="w-3.5 h-3.5" /> Ask AI: &ldquo;{q}&rdquo;
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="p-5 space-y-3" data-testid="help-ai-answer">
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-lg bg-[#FFDA00] flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5" style={{ color: "#032866" }} />
        </span>
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
          {state.used_llm ? "Avida AI" : "Fallback"}
        </p>
      </div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="help-ai-answer-text">
        {state.answer}
      </div>
      {state.suggestedPath && (
        <button
          onClick={() => onGo(state.suggestedPath)}
          className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#032866] text-white text-xs font-semibold hover:bg-[#003A8A]"
          style={{ borderBottom: "2px solid #FFDA00" }}
          data-testid="help-ai-suggested-cta"
        >
          Go to {state.suggestedTitle || "suggested page"} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
